
import React, { useState, useMemo } from 'react';
import { Search, Clock } from 'lucide-react';
import { EMOJI_LIST, EMOJI_CATEGORIES, EmojiItem } from '../../utils/emojiList';

interface EmojiPickerProps {
    onSelect: (emoji: string) => void;
    onClose?: () => void;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect, onClose }) => {
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('smileys');
    const [recent, setRecent] = useState<string[]>(() => {
        const saved = localStorage.getItem('bx_recent_emojis');
        return saved ? JSON.parse(saved) : [];
    });

    const handleSelect = (emoji: string) => {
        onSelect(emoji);
        
        // Update recent
        const newRecent = [emoji, ...recent.filter(e => e !== emoji)].slice(0, 18);
        setRecent(newRecent);
        localStorage.setItem('bx_recent_emojis', JSON.stringify(newRecent));
    };

    const filteredEmojis = useMemo(() => {
        if (!search.trim()) {
            return activeCategory === 'recent' 
                ? recent.map(char => EMOJI_LIST.find(e => e.char === char) || { char, keywords: '', category: 'recent' })
                : EMOJI_LIST.filter(e => e.category === activeCategory);
        }
        
        const term = search.toLowerCase();
        return EMOJI_LIST.filter(e => e.keywords.includes(term));
    }, [search, activeCategory, recent]);

    return (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-slate-200 shadow-xl rounded-xl w-72 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 origin-top-right h-80">
            {/* Search Bar */}
            <div className="p-2 border-b border-slate-100 bg-slate-50">
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Поиск эмоджи..."
                        className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all placeholder:text-slate-400"
                        autoFocus
                    />
                </div>
            </div>

            {/* Categories */}
            {!search && (
                <div className="flex overflow-x-auto scrollbar-hide border-b border-slate-100 bg-white">
                    {EMOJI_CATEGORIES.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`flex-shrink-0 p-2 text-lg hover:bg-slate-50 transition-colors border-b-2 ${
                                activeCategory === cat.id ? 'border-blue-500 bg-blue-50/50' : 'border-transparent opacity-70'
                            }`}
                            title={cat.label}
                        >
                            {cat.icon}
                        </button>
                    ))}
                </div>
            )}

            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar bg-white">
                {search && filteredEmojis.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                        Ничего не найдено
                    </div>
                )}
                
                {activeCategory === 'recent' && !search && recent.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-xs flex flex-col items-center">
                        <Clock className="w-8 h-8 mb-2 opacity-30" />
                        Нет недавних эмоджи
                    </div>
                )}

                <div className="grid grid-cols-8 gap-1">
                    {filteredEmojis.map((emoji, idx) => (
                        <button
                            key={`${emoji.char}-${idx}`}
                            onClick={() => handleSelect(emoji.char)}
                            className="aspect-square flex items-center justify-center text-xl hover:bg-blue-50 rounded-lg transition-transform hover:scale-110 active:scale-95"
                            title={emoji.keywords}
                        >
                            {emoji.char}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
